import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { ASSET, track } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Clock, Users, MapPin, Calendar, ArrowLeft, Wine, Hotel, ChefHat, Utensils, ShoppingBag } from "lucide-react";

const TYPE_LABEL = {
  resort: { label: "Wine Resort", icon: Hotel },
  cantina_visit: { label: "Visita in Cantina", icon: Wine },
  cooking_class: { label: "Cooking Class", icon: ChefHat },
  truffle_hunting: { label: "Truffle Hunting", icon: Utensils },
  oil_tasting: { label: "Olive Oil Tasting", icon: Utensils },
  harvest: { label: "Vendemmia", icon: Wine },
};

export default function EsperienzaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(2);

  useEffect(() => {
    setLoading(true);
    api.get(`/experiences/${id}`)
      .then((r) => setExp(r.data))
      .catch(() => setExp(null))
      .finally(() => setLoading(false));
    if (id) track("experience", id, "view");
  }, [id]);

  if (loading) return <div className="container-xl py-20 text-center text-muted-foreground">Caricamento esperienza…</div>;
  if (!exp) return (
    <div className="container-xl py-20 text-center">
      <h1 className="editorial-heading text-3xl mb-4">Esperienza non trovata</h1>
      <Link to="/esperienze" className="text-[hsl(var(--primary))] underline">Torna alle esperienze</Link>
    </div>
  );

  const t = TYPE_LABEL[exp.type] || { label: "Esperienza", icon: Wine };
  const Icon = t.icon;
  const total = (exp.price || 0) * guests;

  const addToCart = () => {
    if (!date) {
      toast.error("Seleziona una data per la prenotazione");
      return;
    }
    add({
      product_id: exp.id,
      product_type: "experience",
      name: `${exp.name} · ${new Date(date).toLocaleDateString("it-IT")}`,
      image: exp.image,
      unit_price: exp.price,
      quantity: guests,
      unit: "pax",
      meta: { date, guests, experience_type: exp.type, location: `${exp.region} · ${exp.winery}` },
    });
    toast.success(`${exp.name} aggiunto al carrello`);
    setOpen(true);
  };

  return (
    <div data-testid="esperienza-detail-page" className="bg-background">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={ASSET(exp.image)} alt={exp.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/15" />
        <div className="relative container-xl h-full flex flex-col justify-end pb-10 md:pb-14 text-white">
          <Link to="/esperienze" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-white/70 hover:text-white mb-5">
            <ArrowLeft className="h-3.5 w-3.5" /> Torna alle esperienze
          </Link>
          <div className="inline-flex items-center gap-2 chip bg-white/95 text-[hsl(var(--primary))] w-fit mb-3">
            <Icon className="h-3 w-3" /> {t.label}
          </div>
          <h1 data-testid="exp-detail-title" className="editorial-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-balance max-w-4xl">{exp.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{exp.region} · {exp.winery}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{exp.duration}</span>
            <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Fino a {exp.guests_max} ospiti</span>
          </div>
        </div>
      </section>

      {/* CONTENT + BOOKING */}
      <section className="container-xl py-10 md:py-16 grid lg:grid-cols-5 gap-8 lg:gap-12">
        {/* Description column */}
        <div className="lg:col-span-3 space-y-8">
          <div>
            <h2 className="editorial-heading text-2xl md:text-3xl mb-3">L'esperienza</h2>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{exp.description}</p>
          </div>

          {Array.isArray(exp.includes) && exp.includes.length > 0 && (
            <div>
              <h3 className="editorial-heading text-xl mb-3">Cosa include</h3>
              <ul className="space-y-2">
                {exp.includes.map((it, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="text-[hsl(var(--accent))]">•</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-border pt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-card border border-border rounded-sm">
              <Clock className="h-5 w-5 text-[hsl(var(--primary))] mx-auto mb-2" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Durata</div>
              <div className="font-semibold mt-1">{exp.duration}</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-sm">
              <Users className="h-5 w-5 text-[hsl(var(--primary))] mx-auto mb-2" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Ospiti max</div>
              <div className="font-semibold mt-1">{exp.guests_max}</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-sm">
              <MapPin className="h-5 w-5 text-[hsl(var(--primary))] mx-auto mb-2" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Regione</div>
              <div className="font-semibold mt-1 truncate">{exp.region}</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-sm">
              <Wine className="h-5 w-5 text-[hsl(var(--primary))] mx-auto mb-2" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Cantina</div>
              <div className="font-semibold mt-1 truncate">{exp.winery}</div>
            </div>
          </div>
        </div>

        {/* Booking column */}
        <aside className="lg:col-span-2">
          <div data-testid="booking-card" className="bg-card border border-border rounded-sm p-6 lg:sticky lg:top-24">
            <div className="flex items-baseline gap-2 mb-1">
              <div className="text-3xl font-bold editorial-heading text-[hsl(var(--primary))]">€{exp.price.toFixed(0)}</div>
              <div className="text-sm text-muted-foreground">/ persona</div>
            </div>
            <div className="text-xs text-muted-foreground mb-6">Cancellazione gratuita fino a 7 giorni prima</div>

            <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <Calendar className="h-3 w-3" /> Data preferita
            </label>
            <Input
              data-testid="exp-booking-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
              className="mb-4"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <Users className="h-3 w-3" /> Numero ospiti
            </label>
            <div className="flex items-center gap-2 mb-6">
              <button data-testid="guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="h-10 w-10 border border-border rounded-sm hover:bg-muted">−</button>
              <Input data-testid="exp-booking-guests" type="number" min={1} max={exp.guests_max} value={guests} onChange={(e) => setGuests(Math.max(1, Math.min(exp.guests_max, Number(e.target.value) || 1)))} className="text-center font-semibold" />
              <button data-testid="guests-plus" onClick={() => setGuests((g) => Math.min(exp.guests_max, g + 1))} className="h-10 w-10 border border-border rounded-sm hover:bg-muted">+</button>
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4 mb-4">
              <span className="text-sm text-muted-foreground">{guests} × €{exp.price.toFixed(0)}</span>
              <span className="text-2xl font-bold editorial-heading text-[hsl(var(--primary))]" data-testid="exp-booking-total">€{total.toFixed(0)}</span>
            </div>

            <Button data-testid="exp-book-btn" size="lg" onClick={addToCart} className="w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 rounded-sm h-12 gap-2 font-semibold">
              <ShoppingBag className="h-4 w-4" /> Prenota e aggiungi al carrello
            </Button>
            <Button data-testid="exp-buy-now-btn" variant="outline" size="lg" onClick={() => { addToCart(); setTimeout(() => nav("/checkout"), 200); }} className="w-full mt-2 rounded-sm h-12">
              Procedi al checkout
            </Button>
            <div className="text-[10px] text-center text-muted-foreground mt-4 uppercase tracking-[0.2em]">
              Conferma immediata · Pagamento Stripe sicuro
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
